/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Menu,
  Box,
  ListItemText,
  Typography,
  Button,
  List,
  ListItemAvatar,
  Avatar,
  ListItem,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useDispatch } from 'react-redux';
import globalAction from '../store/actions/GlobalAction';

function Notifications({ notifyOpen, anchorE2, apiResponse, onCloseModal }) {
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    lastPage: 1,
    perPage: 5
  });
  const listRef = useRef(null);
  const observer = useRef(null);

  useEffect(() => {
    if (apiResponse && apiResponse.data && Array.isArray(apiResponse.data)) {
      setNotifications(apiResponse.data);
      setCurrentPage(apiResponse.current_page || 1);
      setPagination({
        total: apiResponse.total || 0,
        lastPage: apiResponse.last_page || 1,
        perPage: apiResponse.per_page || 5
      });
      const hasMorePages = (apiResponse.current_page || 1) < (apiResponse.last_page || 1);
      setHasMore(hasMorePages);
    }
  }, [apiResponse]);

  const handleNotifyUpdate = (id) => {
    dispatch(globalAction.readNotification(id));
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, is_unread: false } : notification
      )
    );
  };

  const handleMarkAsAllRead = () => {
    dispatch(globalAction.readAllNotifications());
    setNotifications((prev) => prev.map((notification) => ({ ...notification, is_unread: false })));
  };

  const loadMoreNotifications = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const nextPage = currentPage + 1;
      const result = await dispatch(globalAction.loadMoreNotifications(nextPage));
      if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
        setNotifications((prev) => [...prev, ...result.data]);
        setCurrentPage(result.current_page || nextPage);
        setPagination({
          total: result.total || pagination.total,
          lastPage: result.last_page || pagination.lastPage,
          perPage: result.per_page || pagination.perPage
        });
        const hasMorePages =
          (result.current_page || nextPage) < (result.last_page || pagination.lastPage);
        setHasMore(hasMorePages);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [dispatch, loading, hasMore, currentPage, pagination]);

  const lastNotificationElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loading) {
            loadMoreNotifications();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '20px'
        }
      );
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMoreNotifications]
  );

  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      if (scrollPercentage >= 0.9 && hasMore && !loading) {
        loadMoreNotifications();
      }
    },
    [hasMore, loading, loadMoreNotifications]
  );

  useEffect(() => {
    if (!notifyOpen) {
      if (observer.current) {
        observer.current.disconnect();
      }
    }
  }, [notifyOpen]);

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
      <Menu
        anchorEl={anchorE2}
        id="account-menu"
        open={notifyOpen}
        onClose={onCloseModal}
        PaperProps={{
          elevation: 0,
          sx: {
            maxHeight: '500px',
            overflow: 'visible',
            width: '420px',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1
            },
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header - Sticky */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <ListItem
            secondaryAction={
              <Button
                variant="text"
                size="small"
                onClick={handleMarkAsAllRead}
                disabled={notifications.length === 0}
                edge="end"
                aria-label="Mark all as read"
              >
                Mark All as Read
              </Button>
            }
          >
            <Typography variant="h6">
              Notifications
              {pagination.total > 0 && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: 'text.secondary' }}
                >
                  ({notifications.length} of {pagination.total})
                </Typography>
              )}
            </Typography>
          </ListItem>
        </div>
        {/* Scrollable Notification List */}
        <List
          ref={listRef}
          sx={{
            maxHeight: '350px',
            overflow: 'auto',
            p: 0
          }}
          onScroll={handleScroll}
        >
          {notifications && notifications.length > 0 ? (
            <>
              {notifications.map((item, index) => {
                const isLastItem = index === notifications.length - 1;
                return (
                  <ListItem
                    key={`${item.id}-${item.created_at}`}
                    ref={isLastItem && hasMore ? lastNotificationElementRef : null}
                    divider={!isLastItem || hasMore || loading}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      bgcolor: item.is_unread ? '#e6efff' : '#ffffff',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: item.is_unread ? '#d1e7ff' : '#f5f5f5'
                      }
                    }}
                    secondaryAction={
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onCloseModal();
                        }}
                        component={Link}
                        to={`${item.type}${item.reference_id}`}
                        edge="end"
                        size="small"
                        aria-label="Open notification"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    }
                    onClick={() => {
                      handleNotifyUpdate(item.id);
                    }}
                  >
                    <ListItemAvatar sx={{ mt: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: item.is_unread ? 'primary.main' : 'grey.400',
                          width: 32,
                          height: 32
                        }}
                      >
                        {item.is_unread && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              bgcolor: 'white',
                              borderRadius: '50%'
                            }}
                          />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ whiteSpace: 'normal', pr: 2 }}
                      primary={
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{
                            fontWeight: item.is_unread ? 600 : 400,
                            lineHeight: 1.4,
                            mb: 0.5
                          }}
                          dangerouslySetInnerHTML={{
                            __html: item.message
                          }}
                        />
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '11px',
                            fontWeight: '500',
                            color: item.is_unread ? 'primary.main' : 'text.secondary'
                          }}
                        >
                          {item.created_at}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}

              {/* Loading indicator */}
              {loading && (
                <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more...
                  </Typography>
                </ListItem>
              )}

              {/* Load More Button (as fallback) */}
              {hasMore && !loading && (
                <ListItem sx={{ justifyContent: 'center', py: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadMoreNotifications}
                    sx={{ textTransform: 'none' }}
                  >
                    Load More Notifications
                  </Button>
                </ListItem>
              )}

              {/* End of notifications message */}
              {!hasMore && notifications.length > 0 && !loading && (
                <ListItem sx={{ justifyContent: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {currentPage >= pagination.lastPage
                      ? "You've reached the end"
                      : 'No more notifications'}
                  </Typography>
                </ListItem>
              )}
            </>
          ) : (
            <ListItem sx={{ justifyContent: 'center', py: 4 }}>
              <ListItemText
                sx={{ textAlign: 'center' }}
                primary={
                  <Typography variant="body2" color="text.secondary">
                    No notifications found
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>

        {/* Footer with View All button - Sticky */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'white',
                zIndex: 1,
                padding: '12px'
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<ViewListIcon />}
                component={Link}
                to="/notifications"
                onClick={onCloseModal}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  py: 1
                }}
              >
                View All Notifications
                {pagination.total > notifications.length && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, fontWeight: 'bold' }}>
                    ({pagination.total - notifications.length} more)
                  </Typography>
                )}
              </Button>
            </div>
          </>
        )}
      </Menu>
    </Box>
  );
}
Notifications.propTypes = {
  anchorE2: PropTypes.instanceOf(Element), // more specific than object
  notifyOpen: PropTypes.bool.isRequired,
  apiResponse: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        message: PropTypes.string.isRequired,
        reference_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        is_unread: PropTypes.bool,
        created_at: PropTypes.string.isRequired
      })
    ).isRequired,
    total: PropTypes.number,
    current_page: PropTypes.number,
    last_page: PropTypes.number,
    per_page: PropTypes.number,
    total_count: PropTypes.number
  }).isRequired,
  onCloseModal: PropTypes.func.isRequired
};

Notifications.defaultProps = {
  anchorE2: null
};

export default Notifications;
